import React, { useState, useEffect, Fragment } from "react";
import {
  Menu,
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Link,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility,
  VisibilityOff,
  AccountCircle,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { loginCaptcha, loginInaGeo, logout } from "src/redux/actions/auth";
import { useNavigate } from "react-router-dom";
import UserService from "src/services/user.service";

const LoginModal = ({ anchorEl, open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get auth state
  const { isLoggedIn, user } = useSelector((state) => state.auth);
  const { message } = useSelector((state) => state.message);
  const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const INA_GEO_SITE_KEY = "6LeH8XwhAAAAALw43tTI0iPcLqx8vrlMvkyRwuB6";

  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [inaGeoLoading, setInaGeoLoading] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(""); // Clear error saat user ketik
  };

  // Load reCAPTCHA scripts
  useEffect(() => {
    if (!open) return;

    const loadScriptByURL = (id, url, callback) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        var script = document.createElement("script");
        script.type = "text/javascript";
        script.src = url;
        script.id = id;
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // Load regular reCAPTCHA
    if (SITE_KEY) {
      loadScriptByURL(
        "recaptcha-key",
        `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`,
        () => console.log("Regular reCAPTCHA loaded")
      );
    }

    // Load INA Geo reCAPTCHA
    loadScriptByURL(
      "recaptcha-inageo-key",
      `https://www.google.com/recaptcha/api.js?render=${INA_GEO_SITE_KEY}`,
      () => console.log("INA Geo reCAPTCHA loaded")
    );
  }, [open, SITE_KEY]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!window.grecaptcha) {
      setError("reCAPTCHA belum siap. Silakan coba lagi.");
      setLoading(false);
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(SITE_KEY, { action: "submit" })
        .then((token) => {
          dispatch(loginCaptcha(formData.username, formData.password, token))
            .then(() => {
              setSuccess("Login berhasil!");
              setTimeout(() => {
                setLoading(false);
                onClose();
                // Tetap di halaman saat ini, tidak redirect
              }, 1500);
            })
            .catch((err) => {
              // Error message sudah di-set oleh Redux action
              setLoading(false);
            });
        })
        .catch((err) => {
          setError("Gagal mendapatkan reCAPTCHA token");
          setLoading(false);
        });
    });
  };

  const handleInaGeoLogin = async () => {
    if (!formData.username || !formData.password) {
      setError("Username dan password harus diisi");
      return;
    }

    setInaGeoLoading(true);
    setError("");

    if (!window.grecaptcha) {
      setError("reCAPTCHA belum siap. Silakan coba lagi.");
      setInaGeoLoading(false);
      return;
    }

    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(INA_GEO_SITE_KEY, { action: "submit" })
        .then((token) => {
          dispatch(loginInaGeo(formData.username, formData.password, token))
            .then(() => {
              setSuccess("Login INA Geo berhasil!");
              setTimeout(() => {
                setInaGeoLoading(false);
                onClose();
                // Tetap di halaman saat ini, tidak redirect
              }, 1500);
            })
            .catch((err) => {
              // Error message sudah di-set oleh Redux action
              setInaGeoLoading(false);
            });
        })
        .catch((err) => {
          setError("Gagal mendapatkan reCAPTCHA token");
          setInaGeoLoading(false);
        });
    });
  };

  const handleGoToLoginPage = () => {
    onClose();
    navigate("/auth/login");
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    dispatch(logout());
    setLogoutDialogOpen(false);
    onClose();
    // Smooth transition - tidak langsung muncul form login
    setTimeout(() => {
      // Reset form
      setFormData({ username: "", password: "" });
      setError("");
      setSuccess("");
    }, 300);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleGoToDashboard = () => {
    // Check if user can access dashboard based on their role
    const canAccessDashboard = checkDashboardAccess(user?.roles);
    
    if (canAccessDashboard) {
      onClose();
      navigate("/overview");
    } else {
      setError("Akun Anda tidak memiliki akses ke dashboard. Hanya pengguna dengan domain @big.go.id atau @*.go.id yang dapat mengakses dashboard.");
    }
  };

  // Helper function to format role name
  const getDisplayRoleName = (role) => {
    if (!role) return '';
    
    // Handle both string and object role formats
    const roleStr = typeof role === 'string' ? role : (role?.name || '');
    
    const roleMap = {
      'ROLE_ADMIN': 'Administrator',
      'ROLE_WALIDATA': 'Walidata',
      'ROLE_WALIDATA_PENDUKUNG': 'Walidata Pendukung',
      'ROLE_PRODUSEN': 'Produsen Data',
      'ROLE_EKSTERNAL': 'Publik',
    };
    
    return roleMap[roleStr] || (typeof roleStr === 'string' ? roleStr.replace('ROLE_', '') : roleStr);
  };

  // Get display role names from roles array
  const getDisplayRoles = (roles) => {
    if (!roles || !Array.isArray(roles)) return 'No Role';
    return roles.map(role => getDisplayRoleName(role)).join(', ');
  };

  // Helper function to check dashboard access
  const checkDashboardAccess = (roles) => {
    if (!roles || !Array.isArray(roles)) return false;
    
    const dashboardRoles = [
      'ROLE_ADMIN',
      'ROLE_WALIDATA',
      'ROLE_WALIDATA_PENDUKUNG',
      'ROLE_PRODUSEN'
    ];
    
    return roles.some(role => {
      // Handle both string and object role formats
      const roleStr = typeof role === 'string' ? role : (role?.name || '');
      return dashboardRoles.includes(roleStr);
    });
  };

  // Render different content based on login status
  const renderContent = () => {
    if (isLoggedIn) {
      // Get avatar
      const avatarUrl = user?.avatar ? UserService.getAvatarUrl(user.avatar) : null;
      
      // Check dashboard access
      const canAccessDashboard = checkDashboardAccess(user?.roles);
      
      // User is logged in - show account info (COMPACT)
      return (
        <Box sx={{ p: 2 }}>
          {/* Error message for no dashboard access */}
          {error && (
            <Alert severity="warning" sx={{ mb: 2, py: 0.5 }}>
              <Typography variant="caption">{error}</Typography>
            </Alert>
          )}
          
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Avatar 
              src={avatarUrl}
              sx={{ 
                width: 64, 
                height: 64, 
                mx: 'auto',
                mb: 1,
                bgcolor: 'primary.main',
                fontSize: '2rem'
              }}
            >
              {!user?.avatar && <PersonIcon sx={{ fontSize: '2rem' }} />}
            </Avatar>
            <Typography variant="body1" fontWeight="600" sx={{ mb: 0.5 }}>
              {user?.fullname || user?.username || "User"}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {user?.email || ""}
            </Typography>
            {/* Show role info with friendly name */}
            <Typography variant="caption" color="primary.main" display="block" sx={{ mt: 0.5 }}>
              {getDisplayRoles(user?.roles)}
            </Typography>
          </Box>

          {/* Only show Dashboard button if user has access */}
          {canAccessDashboard ? (
            <Button
              fullWidth
              variant="contained"
              size="medium"
              onClick={handleGoToDashboard}
              sx={{
                borderRadius: 2,
                py: 1,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 600,
                mb: 1,
              }}
            >
              Buka Dashboard
            </Button>
          ) : (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              <Typography variant="caption">
                Selamat datang! di aplikasi SIBATNAS.
              </Typography>
            </Alert>
          )}

          <Button
            fullWidth
            variant="outlined"
            size="medium"
            onClick={handleLogoutClick}
            sx={{
              borderRadius: 2,
              py: 1,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
              borderWidth: 2,
              color: "error.main",
              borderColor: "error.main",
              "&:hover": {
                borderWidth: 2,
                borderColor: "error.dark",
                bgcolor: "error.light",
              },
            }}
          >
            Logout
          </Button>
        </Box>
      );
    }

    // User is not logged in - show login form (COMPACT)
    return (
      <Box sx={{ p: 2 }}>
        {(error || message) && (
          <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="caption">{error || message}</Typography>
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 1.5, py: 0.5 }}>
            <Typography variant="caption">{success}</Typography>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            required
            size="small"
            sx={{ mb: 1.5 }}
            InputProps={{
              sx: { borderRadius: 1.5 },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            required
            size="small"
            sx={{ mb: 2 }}
            InputProps={{
              sx: { borderRadius: 1.5 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="medium"
            disabled={loading || inaGeoLoading}
            sx={{
              borderRadius: 1.5,
              py: 1,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
              mb: 1,
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CircularProgress size={16} color="inherit" />
                <span>Memproses...</span>
              </Box>
            ) : (
              "Masuk"
            )}
          </Button>

          {/* INA Geo Login Button */}
          <Button
            fullWidth
            variant="contained"
            size="medium"
            disabled={loading || inaGeoLoading}
            onClick={handleInaGeoLogin}
            sx={{
              borderRadius: 1.5,
              py: 1,
              textTransform: "none",
              fontSize: "0.875rem",
              fontWeight: 600,
              bgcolor: "#52c41a",
              "&:hover": {
                bgcolor: "#73d13d",
              },
              mb: 1.5,
            }}
          >
            {inaGeoLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <CircularProgress size={16} color="inherit" />
                <span>Memproses...</span>
              </Box>
            ) : (
              "Masuk dengan Ina Geoportal"
            )}
          </Button>
        </form>

        <Divider sx={{ my: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            atau
          </Typography>
        </Divider>

        <Button
          fullWidth
          variant="text"
          size="small"
          onClick={handleGoToLoginPage}
          sx={{
            textTransform: "none",
            fontSize: "0.75rem",
            color: "text.secondary",
          }}
        >
          Buka Halaman Login
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, textAlign: "center", display: "block" }}
        >
          Belum punya akun?{" "}
          <Link
            href="https://tanahair.indonesia.go.id/portal-web/daftar"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "primary.main",
              textDecoration: "none",
              fontWeight: 600,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Daftar
          </Link>
        </Typography>
      </Box>
    );
  };

  return (
    <Fragment>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        disableAutoFocusItem
        disableRestoreFocus
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 350,
            maxWidth: 380,
            maxHeight: 'none',
            overflow: 'visible',
            borderRadius: 2,
            boxShadow: 4,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          onKeyDown: (e) => {
            // Jangan close menu saat Tab, tapi allow Tab navigation
            if (e.key === 'Tab') {
              e.stopPropagation();
            }
          },
          sx: { p: 0 }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: isLoggedIn ? "primary.main" : "white",
            color: isLoggedIn ? "white" : "text.primary",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountCircle sx={{ fontSize: 32 }} />
            <Typography variant="h6" fontWeight="600">
              {isLoggedIn ? "Akun Saya" : "Masuk"}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: isLoggedIn ? "white" : "inherit" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content - Dynamic based on login status */}
        {renderContent()}
      </Menu>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Konfirmasi Logout</DialogTitle>
        <DialogContent>
          <Typography>
            Apakah Anda yakin ingin keluar dari akun <strong>{user?.username}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="inherit">
            Batal
          </Button>
          <Button onClick={handleLogoutConfirm} variant="contained" color="error">
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default LoginModal;
